<template>
  <div class="flex flex-col" :class="hasGroups && 'gap-4 my-4'">
    <div v-for="(items, group) in groupedCartItems" :key="group" :class="hasGroups && 'border rounded-lg mx-2'">
      <div v-if="hasGroups" class="text-sm px-2 py-1 rounded-t-lg border-b bg-gray-50 mb-1">
        Items for
        <span class="font-bold">{{ formatGroupDate(group) }}</span>
      </div>
      <component
        :is="animated ? 'TransitionGroup' : 'ul'"
        class="divide-y divide-gray-200"
        :class="hasGroups ? 'px-2' : 'px-6'"
        v-bind="animated ? drawerTransitionProps : undefined"
      >
        <li
          v-for="item in items"
          :key="itemKey(item)"
          :ref="(line) => setLineRef(itemKey(item), line)"
          class="transition-opacity duration-300 motion-reduce:transition-none"
          :class="{ 'opacity-40': spotlightDims(item) }"
        >
          <div v-if="item.inventory_error" class="pt-3 flex flex-wrap gap-1">
            <span
              class="bg-yellow-100 border border-yellow-700 text-yellow-700 text-xxs font-manrope uppercase px-1.5 py-0.5 rounded-lg tracking-widest"
            >
              {{ item.inventory_error }}
            </span>
          </div>
          <OrderSummaryItem
            :image-url="itemImageUrl(item)"
            :name="item.product_name || productFromStore(item)?.name || ''"
            :variant-text="variantTextFor(item)"
            :price-formatted="showPrices ? formatItemPrice(item.total_price) : ''"
            :original-price-formatted="showPrices ? originalUnitPriceFor(item) : null"
            :quantity="item.quantity"
            :included-quantity="item.included_quantity || 0"
            :editable="!readOnly && !item.inventory_error"
            :read-only="readOnly"
            :loading="itemKey(item) === updatingKey"
            v-bind="quantityRulesFor(item)"
            :remove-reason="removeReasonFor(item)"
            :allow-edit="canEditItem(item)"
            :spotlight="spotlightKeys.includes(itemKey(item))"
            @update-quantity="onUpdateQuantity(item, $event)"
            @remove="onRemove(item)"
            @edit="$emit('edit-line', item)"
          />
          <div v-if="enableGiftCardView && isGiftCardItem(item)" class="pb-3 -mt-1 pl-[60px] text-left">
            <a class="font-light text-xs underline cursor-pointer" @click.stop="$emit('view-gift-cards')">
              Resend Gift Card
            </a>
          </div>
        </li>
      </component>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { inject } from 'vue'
import { formatPrice } from '@/composables/useCartMutation'
import { serverDayToDateObject } from '@/helpers/dates'
import { fromV2 } from '@/models/productViewModel'
import { trackCartLineQuantityChange } from '@/utils/cartLineAnalytics'
import { lineQuantityRules } from '@/utils/cartLineQuantity'
import OrderSummaryItem from '@/components/Checkout/OrderSummaryItem.vue'
import { TransitionGroup } from 'vue'

export default {
  components: { OrderSummaryItem, TransitionGroup },
  setup() {
    const v2UpdateCartItem = inject('v2UpdateCartItem', null)
    const v2IsSubmittingCart = inject('v2IsSubmittingCart', null)
    return { v2UpdateCartItem, v2IsSubmittingCart }
  },
  props: {
    cartItems: {
      type: Array,
      required: true
    },
    storeData: {
      type: Object,
      default: null
    },
    showPrices: {
      type: Boolean,
      default: true
    },
    enableGiftCardView: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    newestFirst: {
      type: Boolean,
      default: false
    },
    animated: {
      type: Boolean,
      default: false
    },
    enforceQuantityRules: {
      type: Boolean,
      default: false
    },
    allowSoldOutRemoval: {
      type: Boolean,
      default: false
    },
    trackQuantityChanges: {
      type: Boolean,
      default: false
    },
    allowEdit: {
      type: Boolean,
      default: false
    },
    // Keys of the lines to pick out for a moment: they flash and the others fade back, so a
    // customer sent here from a menu card sees which lines are that product's (BOT-1748).
    spotlightKeys: {
      type: Array,
      default: () => []
    }
  },
  emits: ['view-gift-cards', 'edit-line'],
  data() {
    return { updatingKey: null }
  },
  created() {
    this.lineRefs = {}
  },
  computed: {
    // Drawer-only: without `animated` the list renders the same plain <ul> it
    // always has, so non-drawer surfaces (v2 checkout, prior orders) are untouched.
    drawerTransitionProps() {
      return {
        tag: 'ul',
        enterActiveClass: 'transition-all duration-300 ease-out',
        enterFromClass: 'opacity-0 -translate-y-3',
        enterToClass: 'opacity-100 translate-y-0',
        moveClass: 'transition-transform duration-300'
      }
    },
    cartSubmitting() {
      if (this.v2IsSubmittingCart !== null) return this.v2IsSubmittingCart
      return this.$store?.state?.activelySubmittingCart ?? false
    },
    currency() {
      return this.storeData?.currency || 'USD'
    },
    storeProducts() {
      return (this.storeData?.categories || []).flatMap((category) => category.products || [])
    },
    productsById() {
      return Object.fromEntries(this.storeProducts.map((product) => [product.id, product]))
    },
    productsByCpspId() {
      return Object.fromEntries(
        this.storeProducts.filter((product) => product.cpsp_id != null).map((product) => [product.cpsp_id, product])
      )
    },
    variantOptionMap() {
      const map = {}
      for (const category of this.storeData?.categories || []) {
        for (const product of category.products || []) {
          for (const variant of product.variants || []) {
            for (const option of variant.variant_options || []) {
              map[option.id] = {
                name: option.name,
                description: option.description,
                variantName: variant.name
              }
            }
          }
        }
      }
      return map
    },
    groupedCartItems() {
      const sorted = _.sortBy(this.cartItems || [], (i) => i.fulfillment_group_date || '')
      const grouped = _.groupBy(sorted, (i) => i.fulfillment_group_date || 'default')
      if (this.newestFirst) {
        Object.keys(grouped).forEach((key) => grouped[key].reverse())
      }
      return grouped
    },
    hasGroups() {
      const keys = Object.keys(this.groupedCartItems).filter((k) => k !== 'default')
      return keys.length > 1
    },
    renderedItems() {
      return Object.values(this.groupedCartItems).flat()
    },
    spotlitItems() {
      return this.renderedItems.filter((item) => this.spotlightKeys.includes(this.itemKey(item)))
    }
  },
  watch: {
    cartSubmitting(val) {
      if (!val) this.updatingKey = null
    }
  },
  methods: {
    itemKey(item) {
      // frontend_key distinguishes multiple customizations of the same product;
      // fall back to cpsp+product for server items that don't carry it.
      return item.frontend_key || `${item.category_product_store_pair_id}-${item.product_id}`
    },
    productFromStore(item) {
      return this.productsById[item.product_id] || null
    },
    setLineRef(key, line) {
      if (line) this.lineRefs[key] = line
      else delete this.lineRefs[key]
    },
    spotlightDims(item) {
      return this.spotlitItems.length > 0 && !this.spotlightKeys.includes(this.itemKey(item))
    },
    // Scrolls the first of these lines into view, for a customer sent here from a menu card (BOT-1748).
    revealLines(keys) {
      const first = this.renderedItems.find((item) => keys.includes(this.itemKey(item)))
      if (first) this.lineRefs[this.itemKey(first)]?.scrollIntoView({ block: 'nearest' })
    },
    storeProductForLine(item) {
      return this.productsByCpspId[item.category_product_store_pair_id] || this.productFromStore(item)
    },
    quantityRulesFor(item) {
      if (!this.enforceQuantityRules) return {}
      const product = this.storeProductForLine(item)
      if (!product) return {}
      return lineQuantityRules(fromV2({ product, cartItem: item }))
    },
    removeReasonFor(item) {
      if (!this.allowSoldOutRemoval || this.readOnly || !item.inventory_error) return null
      return 'sold out'
    },
    canEditItem(item) {
      const product = this.storeProductForLine(item)
      return (
        this.allowEdit &&
        !this.readOnly &&
        !!product &&
        !this.isGiftCardItem(item) &&
        product.type !== 'GiftCardProduct'
      )
    },
    isUpsellLine(item) {
      return String(item.frontend_key || '').includes('-upsell')
    },
    trackQuantityChange(item, quantity) {
      if (!this.trackQuantityChanges) return
      const product = this.storeProductForLine(item)
      trackCartLineQuantityChange(this.$store, {
        from: item.quantity,
        to: quantity,
        isUpsell: this.isUpsellLine(item),
        productId: item.product_id ?? product?.id,
        productName: item.product_name || product?.name
      })
    },
    itemImageUrl(item) {
      // The v2 cart response doesn't carry an image — fall back to the store
      // snapshot's product attachments.
      return item.product_image_url || this.productFromStore(item)?.attachments?.[0]?.url || null
    },
    variantTextFor(item) {
      const options = this.variantOptionsForItem(item)
      if (!options.length) return null
      return options
        .map((o) => {
          const desc = o.description ? ` (${o.description})` : ''
          const qty = (o.quantity || 0) > 1 ? ` x ${o.quantity}` : ''
          return `${o.name}${desc}${qty}`
        })
        .join(', ')
    },
    originalUnitPriceFor(item) {
      if (!this.shouldStrike(item)) return null
      const dp = item.display_price
      const perUnit = dp?.strike_through_price
      if (perUnit == null) return null
      const originalLineCents = perUnit * (item.quantity || 1)
      if (originalLineCents <= item.total_price) return null
      return this.formatItemPrice(originalLineCents)
    },
    onUpdateQuantity(item, quantity) {
      this.updatingKey = this.itemKey(item)
      this.applyQuantity(item, quantity)
    },
    onRemove(item) {
      this.updatingKey = this.itemKey(item)
      this.applyQuantity(item, 0)
    },
    applyQuantity(item, quantity) {
      this.trackQuantityChange(item, quantity)
      const updatedItem = Object.assign({}, item, { quantity })
      if (this.v2UpdateCartItem) {
        // updatingKey is cleared by the cartSubmitting watcher once the
        // debounced POST settles (v2IsSubmittingCart true -> false).
        this.v2UpdateCartItem(updatedItem)
      } else {
        this.$store.dispatch('updateSubmittableCart', { submittableCartItem: updatedItem, forceCheckout: true })
      }
    },
    isGiftCardItem(item) {
      return item?.product_type === 'GiftCardProduct'
    },
    formatGroupDate(group) {
      if (!group || group === 'default') return ''
      return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      }).format(serverDayToDateObject(group))
    },
    variantOptionsForItem(item) {
      const options = []
      for (const variant of item.variants || []) {
        for (const vo of variant.variant_options || []) {
          const info = this.variantOptionMap[vo.id]
          if (info) {
            options.push({
              name: info.name,
              description: info.description,
              variantName: info.variantName,
              quantity: vo.quantity
            })
          }
        }
      }
      return options
    },
    formatItemPrice(cents) {
      return formatPrice(cents, this.currency)
    },
    shouldStrike(item) {
      const dp = item.display_price
      if (!dp || dp.price == null || dp.strike_through_price == null) return false
      return dp.price !== dp.strike_through_price
    },
    strikethroughUnitPrice(item) {
      const dp = item.display_price
      return dp?.strike_through_price_formatted?.format || this.formatItemPrice(dp?.strike_through_price)
    }
  }
}
</script>
